/**
 * Contexto de autorización (RBAC). Carga /api/me una vez y expone el rol y los
 * permisos del usuario para condicionar nav, rutas y acciones en el front.
 *
 * OJO: el front solo OCULTA/INHABILITA. La autorización real la impone el backend
 * (cada endpoint exige su permiso). Esto es UX, no seguridad.
 */
import { useQuery } from "@tanstack/react-query";
import { createContext, useContext, useMemo, type ReactNode } from "react";

import { getMe, type Me } from "./api";

export const ROL_LABEL: Record<string, string> = {
  admin: "Administrador",
  front_office: "Front Office",
  front_office_plus: "Front Office+",
  back_office: "Back Office",
  back_office_plus: "Back Office+",
  consulta: "Consulta",
};

type Authz = {
  me: Me | null;
  rol: string | null;
  has: (perm: string) => boolean;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
};

const Ctx = createContext<Authz | null>(null);

export function AuthzProvider({ children }: { children: ReactNode }) {
  const q = useQuery({
    queryKey: ["me"],
    queryFn: getMe,
    staleTime: 60_000,
    retry: false,
  });

  const value = useMemo<Authz>(() => {
    const me = q.data ?? null;
    const permisos = new Set(me?.permisos ?? []);
    return {
      me,
      rol: me?.rol ?? null,
      has: (perm: string) => permisos.has(perm),
      isLoading: q.isLoading,
      isError: q.isError,
      error: (q.error as Error) ?? null,
      refetch: () => void q.refetch(),
    };
  }, [q.data, q.isLoading, q.isError, q.error, q.refetch]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePermisos(): Authz {
  const v = useContext(Ctx);
  if (!v) throw new Error("usePermisos() debe usarse dentro de <AuthzProvider>");
  return v;
}
